import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { clsx } from 'clsx';
import { gettext } from "../../utils/text";


const DestinationCertificateType = ({
    title,
    enabled,
    onChange = null,
    onUp = null,
    onDown = null,
    onDrag = null,
    onDrop = null,
}) => {
    const [draggedOver, setDraggedOver] = React.useState(false);
    const onDragEnter = React.useCallback((e) => {e.preventDefault(); setDraggedOver(true);}, []);
    const onDragLeave = React.useCallback((e) => {e.preventDefault(); setDraggedOver(false);}, []);
    const onDragOver = React.useCallback((e) => e.preventDefault(), []);
    const onDropInner = React.useCallback((e) => {setDraggedOver(false); onDrop?.(e); }, [onDrop]);
    return (
        <>
            <div
                className={clsx("destination-certificate-dropzone", {"dragged-over": draggedOver})}
                onDragEnter={onDragEnter}
                onDragLeave={onDragLeave}
                onDragOver={onDragOver}
                onDrop={onDropInner}
            />
            <div className="arrangeable-destination-certificate" draggable onDrag={onDrag ?? undefined}>
                <button type="button" className="btn btn-default" disabled={onUp===null} onClick={onUp}><i className="icon icon-arrow-up-circle"></i></button>
                <button type="button" className="btn btn-default" disabled={onDown===null} onClick={onDown}><i className="icon icon-arrow-down-circle"></i></button>
                <div className="form-group">
                    <div className="checkbox">
                        <label>
                            <input type="checkbox" checked={enabled} disabled={onChange===null} onChange={onChange} className="checkboxinput"/>
                            <span className="checkbox-material"><span className="check" /></span>
                            {title}
                        </label>
                    </div>
                </div>
            </div>
        </>
    );
}

const makeCurrentValue = (value) => {
    const normalCertificateTypeOrder = DJ_CONST.certificate_types.map(certificateType => certificateType.type);
    return DJ_CONST.certificate_types.map(certificateType => ({
        type: certificateType.type,
        enabled: value.includes(certificateType.type),
        title: certificateType.title,
    })).toSorted(
        (a, b) => {
            let aIndex = value.indexOf(a.type);
            let bIndex = value.indexOf(b.type);
            if (aIndex === -1) {
                aIndex = value.length + normalCertificateTypeOrder.indexOf(a.type);
            }
            if (bIndex === -1) {
                bIndex = value.length + normalCertificateTypeOrder.indexOf(b.type);
            }

            return aIndex - bIndex;
        }
    );
}

const DestinationCertificateTypes = ({initialValue}) => {
    const [currentValue, setCurrentValue] = React.useState(makeCurrentValue(initialValue));
    React.useEffect(() => setCurrentValue(makeCurrentValue(initialValue)), [initialValue]);
    const currentValueSerialized = React.useMemo(() => JSON.stringify(
        currentValue.filter(
            certificateType => certificateType.enabled
        ).map(certificateType => certificateType.type)),
        [currentValue],
    );

    const onChange = React.useCallback(({
        certificateType,
    }) => {
        setCurrentValue(previous => previous.map(
            ct => ({...ct, enabled: ct.type === certificateType ? !ct.enabled : ct.enabled})
        ))
    }, [])
    const onUp = React.useCallback(({
        certificateType,
    }) => {
        setCurrentValue(previous => {
            const currentIndex = previous.map(ct => ct.type).indexOf(certificateType);
            const result = [...previous];
            if (currentIndex > 0) {
                [result[currentIndex], result[currentIndex - 1]] = [result[currentIndex - 1], result[currentIndex]]
            }
            return result;
        })
    }, [])
    const onDown = React.useCallback(({
        certificateType,
    }) => {
        setCurrentValue(previous => {
            const currentIndex = previous.map(ct => ct.type).indexOf(certificateType);
            const result = [...previous];
            if (currentIndex < result.length - 1) {
                [result[currentIndex], result[currentIndex + 1]] = [result[currentIndex + 1], result[currentIndex]]
            }
            return result;
        })
    }, [])

    const draggedIndex = React.useRef(null);
    const onDrag = React.useCallback(({index}) => {draggedIndex.current = index}, [])
    const onDrop = React.useCallback(({
        targetIndex,
    }) => {
        if (draggedIndex.current === null || draggedIndex.current === targetIndex) {
            return;
        }
        setCurrentValue(previous => {
            if (targetIndex < 0 || targetIndex >= previous.length) {
                return previous;
            }
            const item = previous[draggedIndex.current];
            const previousWithoutDragged = [...previous.slice(0, draggedIndex.current), ...previous.slice(draggedIndex.current + 1)];
            const adjustedTargetIndex = draggedIndex.current > targetIndex ? targetIndex : targetIndex + 1;

            draggedIndex.current = null;
            return [...previousWithoutDragged.slice(0, adjustedTargetIndex), item, ...previousWithoutDragged.slice(adjustedTargetIndex)];
        });
    }, [])

    return (<>
        <h5 className="with-margin-top inline">{gettext("Certificates")}</h5>
        {currentValue.map(((certificateType, index) => (<DestinationCertificateType
            key={certificateType.type}
            title={certificateType.title}
            enabled={certificateType.enabled}
            onChange={() => onChange({certificateType: certificateType.type})}
            onUp={index === 0 ? null : () => onUp({certificateType: certificateType.type})}
            onDown={index === currentValue.length - 1 ? null : () => onDown({certificateType: certificateType.type})}
            onDrag={() => onDrag({index})}
            onDrop={() => onDrop({targetIndex: index})}
        />)))}
        <input type="hidden" id="id_certificate_types" name="certificate_types" value={currentValueSerialized} />
    </>);
}


export const renderDestinationCertificateTypes = () => {
    // This is rendered by crispy_full_field, see destination_modal.html
    const certificateTypesFullField = document.getElementById('div_id_certificate_types');
    const certificateTypesField = document.getElementById('id_certificate_types');

    const initialValue = certificateTypesField ? JSON.parse(certificateTypesField.value) : [];

    if (certificateTypesField && certificateTypesFullField) {
        ReactDOM.render(
            <DestinationCertificateTypes initialValue={initialValue} />,
            certificateTypesFullField,
        );
    }

    /* On value change, re-render the whole component, which is more straightforward than trying to manage the state
    * inside the component. */
    return { setInitialValue: (newInitialValue) => {
        ReactDOM.render(
            <DestinationCertificateTypes initialValue={newInitialValue} />,
            certificateTypesFullField,
        );
    } }
}
